import React, { useEffect, useMemo, useRef, useState } from "react";
import Icon from "@mdi/react";
import {
  mdiAlertCircle,
  mdiAlertOutline,
  mdiBarcodeScan,
  mdiCheckCircleOutline,
  mdiCloseCircle,
} from "@mdi/js";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import { ScannedBarCodeScanForDeviceGRNAPI } from "../../../../erp/accounts/vouchers/purchase/service/inward_repo";
import { useWarehouseInward } from "../contexts/InwardContext";
import { calculateIsBlockedByDispute } from "../warehouse_helper";
import Papa from "papaparse";
import DeviceIdsViewer from "../common-components/DeviceIdsViewer";
import { QrScanner } from "@yudiel/react-qr-scanner";
import DeviceScanModalInward from "./DeviceScanInwradModal";
import CurrencyBadge from "./CurrencyBadge";
import { ItemWarehouseModel } from "../model/warehouse_inward_models";

interface Props {
  item: ItemWarehouseModel;

  isSelected: boolean;
  onSelect: (checked: boolean) => void;

  invoiceQty?: number;
  invoiceRate?: number;
  physicalQty?: number;
  invoiceTotalAmount?: number;

  allocatedDevices?: string[];
  inwardedDevices?: string[];

  error?: string;

  onInvoiceQtyChange: (qty: number) => void;
  onRateChange: (rate: number) => void;
  onPhysicalQtyChange: (qty: number) => void;
  onTotalAmountChange: (val: number) => void;
  onDeviceSelection: (selection: DeviceSelection) => void;
  currency?: string;
  vendor_currency_to_inr?: number;
}

export interface DeviceSelection {
  matched: string[];
  unmatched: string[];
  quantity: number;
}

interface ValidationResult {
  valid: boolean;
  reason?: "not_allocated" | "already_inwarded" | "duplicate_scan";
  deviceId?: string;
}

interface RejectedDevice {
  id: string;
  reason: string;
}
const InfraInwardReceiveItemCard: React.FC<Props> = ({
  item,
  isSelected,
  onSelect,
  onDeviceSelection,
  invoiceQty = 0,
  invoiceRate = 0,
  physicalQty = 0,
  invoiceTotalAmount = 0,
  inwardedDevices,
  error,
  allocatedDevices,
  onInvoiceQtyChange,
  onRateChange,
  onPhysicalQtyChange,
  onTotalAmountChange,
  currency,
  vendor_currency_to_inr,
}) => {
  const ordered = item.requestedQty ?? 0;
  const received = item.receivedQty ?? 0;
  const pending = Math.max(ordered - received, 0);

  // ✅ PO Logic (taken from your ST/PO component)
  const isInvoiceExceeded = invoiceQty > pending;
  const isMismatch =
    invoiceQty > 0 && physicalQty > 0 && invoiceQty !== physicalQty;

  const isAccepted =
    !isMismatch && invoiceQty > 0 && physicalQty > 0 && physicalQty <= pending;
  const { showToast } = useToast();

  // Local UI state
  const [scanning, setScanning] = useState(false);
  const [matchedDevices, setMatchedDevices] = useState<string[]>([]);
  const [rejectedDevices, setRejectedDevices] = useState<RejectedDevice[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const processingRef = useRef<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<"scan" | "bulk" | "upload">(
    "scan",
  );
  const [bulkInput, setBulkInput] = useState("");
  const [barcodeInputValue, setBarcodeInputValue] = useState("");
  const barcodeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!scanning) {
      processingRef.current.clear();
      setBarcodeInputValue("");
      setIsProcessing(false);
    }
  }, [scanning]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const clean = barcodeInputValue.trim();
      if (clean && !isProcessing) {
        if (clean.length === 8 || clean.length === 6) {
          handleScanResult(clean);
          setBarcodeInputValue("");
        }
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [barcodeInputValue, isProcessing]);

  const { currentInwardOrder } = useWarehouseInward();

  const isFinishedGoods = item.subCategory === "Finished Goods";
  const isBlockedByDispute = useMemo(
    () =>
      calculateIsBlockedByDispute(
        currentInwardOrder?.past_disputes,
        currentInwardOrder?.is_disputed,
      ),
    [currentInwardOrder?.past_disputes, currentInwardOrder?.is_disputed],
  );

  const displayPhysicalQty = isFinishedGoods
    ? matchedDevices.length
    : physicalQty;

  useEffect(() => {
    // For FG, we strictly follow scanned quantity for physical receipt
    onDeviceSelection({
      matched: matchedDevices,
      unmatched: rejectedDevices.map((d) => d.id),
      quantity: displayPhysicalQty,
    });
  }, [matchedDevices, rejectedDevices, displayPhysicalQty, isFinishedGoods]);

  // Validate device based on inward type
  const validateDevice = (deviceId: string): ValidationResult => {
    // Check if already scanned in this session
    if (matchedDevices.includes(deviceId)) {
      return { valid: false, reason: "duplicate_scan", deviceId };
    }

    // Check if already inwarded in previous GRNs
    if (inwardedDevices?.includes(deviceId)) {
      return { valid: false, reason: "already_inwarded", deviceId };
    }

    // PO-specific validation: Check pending quantity limit
    if (currentInwardOrder?.type === "PO") {
      const remaining = pending - matchedDevices.length;
      if (remaining <= 0) {
        return { valid: false, reason: "duplicate_scan", deviceId }; // Reuse for overflow
      }
    }

    return { valid: true };
  };

  // Get validation error message
  const getValidationMessage = (reason: string): string => {
    const messages: Record<string, string> = {
      not_allocated: `Not allocated to this ${currentInwardOrder?.type}`,
      already_inwarded: "Already inwarded in previous GRN",
      duplicate_scan: "Already scanned in this session",
    };
    return messages[reason] || "Invalid device";
  };

  const processBatchDevices = (
    deviceIds: string[],
  ): {
    valid: string[];
    rejected: RejectedDevice[];
    duplicatesRemoved: number;
  } => {
    const uniqueIds = Array.from(new Set(deviceIds));
    const duplicatesRemoved = deviceIds.length - uniqueIds.length;

    const valid: string[] = [];
    const rejected: RejectedDevice[] = [];

    uniqueIds.forEach((deviceId) => {
      const validation = validateDevice(deviceId);

      if (validation.valid) {
        valid.push(deviceId);
      } else {
        rejected.push({
          id: deviceId,
          reason: getValidationMessage(validation.reason!),
        });
      }
    });

    return { valid, rejected, duplicatesRemoved };
  };

  // Handle PO scan - scans individual device QR
  const handlePOScanResult = async (raw: string) => {
    const clean = raw.trim().replace(/\s+/g, "");

    if (clean.length !== 6 && clean.length !== 8) {
      showToast({ type: ToastType.error, text: "Invalid device QR format" });
      return;
    }

    if (isProcessing) {
      showToast({
        type: ToastType.warning,
        text: "Processing previous scan...",
      });
      return;
    }

    if (processingRef.current.has(clean)) {
      showToast({
        type: ToastType.warning,
        text: "Already processed this QR",
      });
      return;
    }

    setIsProcessing(true);
    processingRef.current.add(clean);

    try {
      let res;
      if (clean.length === 8 || clean.length === 6) {
        res = await ScannedBarCodeScanForDeviceGRNAPI(
          item.inventoryItemCode,
          clean,
        );
      }

      if (!res.success || !Array.isArray(res.devices)) {
        showToast({
          type: ToastType.error,
          text: res.error || "No devices found",
        });
        return;
      }

      let fetchedIds: string[] = [];

      fetchedIds = Array.from(
        new Set(
          res.devices
            .map((d: any) => d?.unique_id)
            .filter((id: any): id is string => typeof id === "string"),
        ),
      );

      if (!fetchedIds.length) {
        showToast({
          type: ToastType.error,
          text: res.error || "No valid devices found",
        });
        return;
      }

      const { valid, rejected, duplicatesRemoved } =
        processBatchDevices(fetchedIds);

      // Check if we can add more devices
      const remaining = invoiceQty - matchedDevices.length;

      if (remaining <= 0) {
        showToast({
          type: ToastType.warning,
          text: "Invoice quantity already fulfilled... Inward will be based on invoice quantity",
        });
        return;
      }

      // Add valid devices (up to remaining quantity)
      const toAdd = valid.slice(0, remaining);
      const overflow = valid.slice(remaining);

      if (toAdd.length > 0) {
        setMatchedDevices((prev) => [...prev, ...toAdd]);
        if (!isFinishedGoods) {
          onPhysicalQtyChange?.(
            Math.min(invoiceQty, physicalQty + toAdd.length),
          );
        }
      }

      // Track rejected devices (including overflow)
      const allRejected = [
        ...rejected,
        ...overflow.map((id) => ({
          id,
          reason: "Exceeds pending quantity",
        })),
      ];

      if (allRejected.length > 0) {
        setRejectedDevices((prev) => [...prev, ...allRejected]);
      }

      // Show comprehensive feedback
      let message = `✅ Valid: ${toAdd.length}`;
      if (rejected.length > 0) message += ` | ❌ Invalid: ${rejected.length}`;
      if (overflow.length > 0) message += ` | ⚠️ Overflow: ${overflow.length}`;
      if (duplicatesRemoved > 0)
        message += ` | 🔄 Duplicates removed: ${duplicatesRemoved}`;

      showToast({
        type: toAdd.length > 0 ? ToastType.success : ToastType.warning,
        text: message,
      });
    } catch (e: any) {
      showToast({
        type: ToastType.error,
        text: e.message || "Device scan failed",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleScanResult = (raw: string) => {
    if (isBlockedByDispute) {
      showToast({
        type: ToastType.error,
        text: "Scanning blocked due to unresolved disputes",
      });
      return;
    }
    handlePOScanResult(raw);
  };

  // Handle bulk paste with deduplication**
  const handleBulkPaste = () => {
    if (!bulkInput.trim()) {
      showToast({
        type: ToastType.warning,
        text: "Please paste device IDs",
      });
      return;
    }

    const ids = bulkInput
      .split(/[\n,;]+/) // Support multiple delimiters
      .map((v) => v.trim())
      .filter(Boolean);

    if (!ids.length) {
      showToast({
        type: ToastType.warning,
        text: "No valid device IDs found",
      });
      return;
    }

    const { valid, rejected, duplicatesRemoved } = processBatchDevices(ids);

    // Apply quantity limits
    const remaining = pending - matchedDevices.length;

    if (remaining <= 0) {
      showToast({
        type: ToastType.warning,
        text:
          currentInwardOrder?.type === "PO"
            ? "Pending quantity already fulfilled"
            : "All allocated devices already scanned",
      });
      return;
    }

    const toAdd = valid.slice(0, remaining);
    const overflow = valid.slice(remaining);

    if (toAdd.length > 0) {
      setMatchedDevices((prev) => [...prev, ...toAdd]);
      if (!isFinishedGoods) {
        onPhysicalQtyChange?.(Math.min(remaining, physicalQty + toAdd.length));
      }
    }

    const allRejected = [
      ...rejected,
      ...overflow.map((id) => ({
        id,
        reason: "Exceeds quantity limit",
      })),
    ];

    if (allRejected.length > 0) {
      setRejectedDevices((prev) => [...prev, ...allRejected]);
    }

    // Show detailed feedback
    let message = `✅ Added: ${toAdd.length}`;
    if (rejected.length > 0) message += ` | ❌ Rejected: ${rejected.length}`;
    if (overflow.length > 0) message += ` | ⚠️ Overflow: ${overflow.length}`;
    if (duplicatesRemoved > 0)
      message += ` | 🔄 Duplicates removed: ${duplicatesRemoved}`;

    showToast({
      type: toAdd.length > 0 ? ToastType.success : ToastType.warning,
      text: message,
    });

    setBulkInput("");
  };

  // Handle CSV upload with progress and deduplication**
  const handleCSVUpload = (file: File) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const rows = result.data as Record<string, any>[];

        if (!rows || rows.length === 0) {
          showToast({
            type: ToastType.error,
            text: "CSV is empty or invalid",
          });
          return;
        }

        if (!("Unique ID" in rows[0])) {
          showToast({
            type: ToastType.error,
            text: 'CSV must contain "Unique ID" column',
          });
          return;
        }

        const parsedIds = rows
          .map((r) => String(r["Unique ID"] || "").trim())
          .filter(Boolean);

        if (!parsedIds.length) {
          showToast({
            type: ToastType.error,
            text: "No valid device IDs found in CSV",
          });
          return;
        }

        const { valid, rejected, duplicatesRemoved } =
          processBatchDevices(parsedIds);

        // Apply quantity limits
        const remaining = pending - matchedDevices.length;

        if (remaining <= 0) {
          showToast({
            type: ToastType.warning,
            text: "Quantity limit already reached",
          });
          return;
        }

        const toAdd = valid.slice(0, remaining);
        const overflow = valid.slice(remaining);

        if (toAdd.length > 0) {
          setMatchedDevices((prev) => [...prev, ...toAdd]);
          if (!isFinishedGoods) {
            onPhysicalQtyChange?.(
              Math.min(remaining, physicalQty + toAdd.length),
            );
          }
        }

        const allRejected = [
          ...rejected,
          ...overflow.map((id) => ({
            id,
            reason: "Exceeds quantity limit",
          })),
        ];

        if (allRejected.length > 0) {
          setRejectedDevices((prev) => [...prev, ...allRejected]);
        }

        // Show comprehensive feedback
        let message = `📄 CSV: ${parsedIds.length} rows | ✅ Added: ${toAdd.length}`;
        if (rejected.length > 0)
          message += ` | ❌ Rejected: ${rejected.length}`;
        if (overflow.length > 0)
          message += ` | ⚠️ Overflow: ${overflow.length}`;
        if (duplicatesRemoved > 0)
          message += ` | 🔄 Duplicates: ${duplicatesRemoved}`;

        showToast({
          type: toAdd.length > 0 ? ToastType.success : ToastType.warning,
          text: message,
        });
      },
      error: () => {
        showToast({
          type: ToastType.error,
          text: "Failed to parse CSV file",
        });
      },
    });
  };

  // Clear all scanned devices
  const handleClearAll = () => {
    if (
      !window.confirm(
        "Are you sure you want to clear all scanned devices? This action cannot be undone.",
      )
    ) {
      return;
    }

    setMatchedDevices([]);
    setRejectedDevices([]);
    processingRef.current.clear();
    showToast({
      type: ToastType.success,
      text: "All devices cleared",
    });
  };

  const handleClearRejected = () => {
    setRejectedDevices([]);
  };

  useEffect(() => {
    setMatchedDevices([]);
    setRejectedDevices([]);
    setBulkInput("");
    processingRef.current.clear();
  }, [item.inventoryItemCode]);
  return (
    <div
      id={`warehouse-inward-infra-item-card-${item.inventoryItemCode}`}
      className={`border rounded-xl p-4 transition-all ${
        isSelected ? "bg-orange-50 border-orange-200" : "bg-white"
      }`}
    >
      {/* Desktop layout */}
      <div className="hidden md:grid grid-cols-12 gap-4 items-center">
        {/* Checkbox */}
        <div className="col-span-1">
          <input
            id={`warehouse-inward-infra-item-${item.inventoryItemCode}-select-desktop`}
            type="checkbox"
            checked={isSelected}
            disabled={pending === 0}
            onChange={(e) => onSelect(e.target.checked)}
            className={`w-4 h-4 ${pending === 0 ? "cursor-not-allowed opacity-40" : ""}`}
          />
        </div>

        {/* Item */}
        <div className="col-span-2">
          <p className="font-semibold">{item.itemName}</p>
          <p className="text-xs text-gray-500">{item.inventoryItemCode}</p>
        </div>

        {/* PO Qty */}
        <div className="col-span-1 text-center">{ordered}</div>

        {/* Prev Recvd */}
        <div className="col-span-1 text-center text-gray-500">{received}</div>

        {/* Remaining */}
        <div className="col-span-1 text-center font-semibold">{pending}</div>

        {/* Invoice Qty */}
        <div className="col-span-1">
          <input
            id={`warehouse-inward-infra-item-${item.inventoryItemCode}-invoice-qty`}
            type="number"
            min={0}
            max={pending}
            value={invoiceQty || ""}
            onChange={(e) => onInvoiceQtyChange(Number(e.target.value))}
            className={`w-full border rounded px-2 py-1 text-center
              ${isInvoiceExceeded ? "border-red-400 bg-red-50" : ""}
            `}
          />
        </div>

        {/* Rate */}
        <div className="col-span-1">
          <input
            id={`warehouse-inward-infra-item-${item.inventoryItemCode}-rate`}
            type="number"
            min={0}
            value={item.itemPrice || ""}
            onChange={(e) => onRateChange(Number(e.target.value))}
            className="w-full border rounded px-2 py-1 text-center"
          />
        </div>

        {/* Physical Qty */}
        <div className="col-span-1">
          <input
            id={`warehouse-inward-infra-item-${item.inventoryItemCode}-physical-qty`}
            type="number"
            min={0}
            max={pending}
            value={physicalQty || ""}
            onChange={(e) => onPhysicalQtyChange(Number(e.target.value))}
            className={`w-full border rounded px-2 py-1 text-center
              ${isMismatch ? "border-orange-400 bg-orange-50" : ""}
            `}
          />
        </div>

        {/* Total */}
        <div className="col-span-2 text-center font-medium">
          ₹ {item?.itemPrice! * item?.requestedQty || 0}
          <CurrencyBadge
            currency={currency}
            rate={vendor_currency_to_inr}
            inrAmount={item?.itemPrice ?? 0}
            perUnit
          />
        </div>

        {/* Status */}
        <div className="col-span-1 flex justify-center">
          {isInvoiceExceeded ? (
            <span className="text-xs text-red-500 font-semibold">Exceeded</span>
          ) : isMismatch ? (
            <span className="flex items-center gap-1 text-orange-500 text-xs font-semibold border border-orange-300 px-2 py-1 rounded-full bg-orange-50">
              <Icon path={mdiAlertOutline} size={0.6} />
              Mismatch
            </span>
          ) : isAccepted ? (
            <span className="flex items-center gap-1 text-green-600 text-xs font-semibold border border-green-300 px-2 py-1 rounded-full bg-green-50">
              <Icon path={mdiCheckCircleOutline} size={0.6} />
              Accepted
            </span>
          ) : null}
        </div>
      </div>

      {/* Mobile layout */}
      <div className="md:hidden flex flex-col gap-3">
        {/* Row 1: checkbox + item name + status */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2">
            <input
              id={`warehouse-inward-infra-item-${item.inventoryItemCode}-select-mobile`}
              type="checkbox"
              checked={isSelected}
              disabled={pending === 0}
              onChange={(e) => onSelect(e.target.checked)}
              className={`mt-1 w-4 h-4 shrink-0 ${pending === 0 ? "cursor-not-allowed opacity-40" : ""}`}
            />
            <div>
              <p className="font-semibold text-sm">{item.itemName}</p>
              <p className="text-xs text-gray-500">{item.inventoryItemCode}</p>
            </div>
          </div>
          <div className="shrink-0">
            {isInvoiceExceeded ? (
              <span className="text-xs text-red-500 font-semibold">
                Exceeded
              </span>
            ) : isMismatch ? (
              <span className="flex items-center gap-1 text-orange-500 text-xs font-semibold border border-orange-300 px-2 py-0.5 rounded-full bg-orange-50">
                <Icon path={mdiAlertOutline} size={0.5} />
                Mismatch
              </span>
            ) : isAccepted ? (
              <span className="flex items-center gap-1 text-green-600 text-xs font-semibold border border-green-300 px-2 py-0.5 rounded-full bg-green-50">
                <Icon path={mdiCheckCircleOutline} size={0.5} />
                Accepted
              </span>
            ) : null}
          </div>
        </div>

        {/* Row 2: qty stats */}
        <div className="grid grid-cols-3 gap-2 text-xs text-center">
          <div className="bg-gray-50 rounded-lg p-2">
            <p className="text-gray-500">PO Qty</p>
            <p className="font-semibold mt-0.5">{ordered}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-2">
            <p className="text-gray-500">Prev. Recd</p>
            <p className="font-semibold mt-0.5">{received}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-2">
            <p className="text-gray-500">Remaining</p>
            <p className="font-semibold mt-0.5">{pending}</p>
          </div>
        </div>

        {/* Row 3: inputs */}
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="flex flex-col gap-1">
            <label className="text-gray-500">Invoice Qty</label>
            <input
              id={`warehouse-inward-infra-item-${item.inventoryItemCode}-invoice-qty-mobile`}
              type="number"
              min={0}
              max={pending}
              value={invoiceQty || ""}
              onChange={(e) => onInvoiceQtyChange(Number(e.target.value))}
              className={`w-full border rounded px-2 py-1.5 text-center text-sm ${isInvoiceExceeded ? "border-red-400 bg-red-50" : ""}`}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-gray-500">Rate</label>
            <input
              id={`warehouse-inward-infra-item-${item.inventoryItemCode}-rate-mobile`}
              type="number"
              min={0}
              value={item.itemPrice || ""}
              onChange={(e) => onRateChange(Number(e.target.value))}
              className="w-full border rounded px-2 py-1.5 text-center text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-gray-500">Physical Qty</label>
            <input
              id={`warehouse-inward-infra-item-${item.inventoryItemCode}-physical-qty-mobile`}
              type="number"
              min={0}
              max={pending}
              value={physicalQty || ""}
              onChange={(e) => onPhysicalQtyChange(Number(e.target.value))}
              className={`w-full border rounded px-2 py-1.5 text-center text-sm ${isMismatch ? "border-orange-400 bg-orange-50" : ""}`}
            />
          </div>
        </div>

        {/* Row 4: value */}
        <div className="text-sm font-medium text-right text-gray-700">
          Value: ₹ {item?.itemPrice! * item?.requestedQty || 0}
          <CurrencyBadge
            currency={currency}
            rate={vendor_currency_to_inr}
            inrAmount={item?.itemPrice ?? 0}
            perUnit
            className="text-right"
          />
        </div>
      </div>

      {/* Quantity Mismatch Error message */}
      {((invoiceQty > 0 && invoiceQty !== pending) ||
        (physicalQty > 0 && physicalQty !== pending)) && (
        <div className="text-warning text-xs">
          <Icon path={mdiAlertOutline} size={0.6} className="inline mr-1" />{" "}
          Quantity mismatch detected
        </div>
      )}

      {/* Rate Matching and Totals */}
      {currentInwardOrder?.type === "PO" && (
        <div className="grid md:grid-cols-3 grid-cols-2 gap-4 text-sm">
          <div className="flex flex-col gap-1 md:gap-2">
            <p className="text-gray-500">PO Rate</p>
            <p className="w-full font-semibold border rounded-md px-3 py-2 input-warning text-center">
              {item.itemPrice}
            </p>
          </div>

          {/* input */}
          <div className="flex flex-col gap-1 md:gap-2">
            <p className="text-gray-500">Invoice Rate / unit</p>
            <input
              id={`warehouse-inward-infra-item-${item.inventoryItemCode}-invoice-rate-unit`}
              type="number"
              min={0}
              step={0.01}
              value={invoiceRate || ""}
              inputMode="decimal"
              disabled={pending === 0 || isBlockedByDispute}
              title={
                isBlockedByDispute
                  ? "Input blocked due to unresolved disputes"
                  : ""
              }
              onChange={(e) => {
                const value = Number(e.target.value);
                onRateChange?.(value);
              }}
              className={`w-full border-2 rounded-md px-3 py-2 focus:outline-none transition-all ${
                pending === 0 || isBlockedByDispute
                  ? "bg-yellow-50 border-yellow-200 text-yellow-700 cursor-not-allowed"
                  : "bg-white border-purple-300 focus:border-primary focus:ring-1 focus:ring-primary font-semibold"
              }`}
            />
          </div>

          <div className="flex flex-col gap-1 md:gap-2">
            <p className="text-gray-500">Total Invoice Amount</p>
            <input
              id={`warehouse-inward-infra-item-${item.inventoryItemCode}-invoice-total`}
              type="number"
              min={0}
              step={0.01}
              value={invoiceTotalAmount || ""}
              inputMode="decimal"
              disabled={pending === 0 || isBlockedByDispute}
              title={
                isBlockedByDispute
                  ? "Input blocked due to unresolved disputes"
                  : ""
              }
              onChange={(e) => {
                const value = Number(e.target.value);
                onTotalAmountChange?.(value);
              }}
              className={`w-full border-2 rounded-md px-3 py-2 focus:outline-none transition-all ${
                pending === 0 || isBlockedByDispute
                  ? "bg-amber-50 border-amber-200 text-amber-700 cursor-not-allowed"
                  : "bg-white border-purple-300 focus:border-primary focus:ring-1 focus:ring-primary font-semibold"
              }`}
            />
          </div>
        </div>
      )}

      {/* Rate Mismatch Error message */}
      {currentInwardOrder?.type === "PO" &&
        invoiceRate > 0 &&
        invoiceRate !== item.itemPrice && (
          <div className="text-warning text-xs">
            <Icon path={mdiAlertOutline} size={0.6} className="inline mr-1" /> ₹
            Rate mismatch detected — PO: ₹{item.itemPrice} vs Invoice: ₹
            {invoiceRate}
          </div>
        )}

      {/* Dispute handled at page level */}
      {isFinishedGoods && (
        <div className="space-y-3">
          {/* Already Inwarded Devices Info (Previous GRNs) */}
          {inwardedDevices && inwardedDevices.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-3 text-sm">
              <DeviceIdsViewer
                deviceIds={inwardedDevices}
                title={`Already Inwarded`}
              />
              <p className="text-[10px] text-slate-500 mt-1 italic">
                * These devices were successfully received in previous partial
                GRNs
              </p>
            </div>
          )}

          {/* Scan Button */}
          <div className="space-y-1.5">
            <button
              id={`warehouse-inward-infra-item-${item.inventoryItemCode}-scan-btn`}
              type="button"
              disabled={
                (currentInwardOrder?.type === "PO" &&
                  pending - matchedDevices.length <= 0) ||
                isProcessing ||
                isBlockedByDispute
              }
              onClick={() => setScanning((v) => !v)}
              className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg font-semibold text-sm transition-all
                ${
                  isBlockedByDispute
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed opacity-75 border border-dashed border-gray-300"
                    : "bg-purple-200 text-purple-700 hover:bg-purple-300 disabled:opacity-50 disabled:cursor-not-allowed"
                }
              `}
              title={
                isBlockedByDispute
                  ? "Scanning blocked due to unresolved disputes"
                  : ""
              }
            >
              <Icon path={mdiBarcodeScan} size={0.7} />
              {isProcessing
                ? "Processing..."
                : scanning
                  ? "Close Scanner"
                  : "Open Scan Interface"}
            </button>
            {isBlockedByDispute && (
              <div className="flex items-center gap-1.5 px-1 py-0.5 text-red-500 animate-in fade-in slide-in-from-top-1">
                <Icon path={mdiAlertCircle} size={0.5} />
                <span className="text-xs text-color-destructive">
                  Scanning Blocked: Resolve disputes to continue
                </span>
              </div>
            )}
          </div>

          {scanning && (
            <div className="mt-4">
              <DeviceScanModalInward
                open={scanning}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                onClose={() => setScanning(false)}
              >
                {/* SCAN TAB */}
                {activeTab === "scan" && (
                  <div className="space-y-4">
                    {/* Hardware Barcode Scanner Input */}
                    <div className="space-y-2 bg-blue-50/50 p-4 rounded-lg border border-blue-100">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon
                          path={mdiBarcodeScan}
                          size={0.6}
                          className="text-blue-600"
                        />
                        <label className="text-xs font-bold text-gray-700 uppercase tracking-tight">
                          Hardware Barcode Scanner
                        </label>
                      </div>
                      <div className="flex w-full">
                        <input
                          id={`warehouse-inward-infra-item-${item.inventoryItemCode}-barcode-input`}
                          ref={barcodeInputRef}
                          type="text"
                          autoFocus
                          value={barcodeInputValue}
                          onChange={(e) => setBarcodeInputValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && barcodeInputValue.trim()) {
                              handleScanResult(barcodeInputValue.trim());
                              setBarcodeInputValue("");
                            }
                          }}
                          placeholder="Scan or type device ID..."
                          className="w-full rounded-md border-2 border-primary/30 p-3 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all shadow-inner bg-white"
                        />
                      </div>
                    </div>

                    <div className="relative flex items-center py-2">
                      <div className="flex-grow border-t border-gray-200"></div>
                      <span className="flex-shrink mx-4 text-gray-400 text-[10px] font-bold uppercase tracking-wider">
                        OR SCAN VIA CAMERA
                      </span>
                      <div className="flex-grow border-t border-gray-200"></div>
                    </div>

                    <div className="rounded-md border-2 border-gray-100 overflow-hidden h-[260px] md:h-68 max-w-sm mx-auto relative group bg-black shadow-lg">
                      <QrScanner
                        constraints={{ facingMode: "environment" }}
                        onDecode={(result) => {
                          if (result && !isProcessing) handleScanResult(result);
                        }}
                        onError={(err) => {
                          console.error(err);
                          showToast({
                            type: ToastType.error,
                            text: "Camera error. Use barcode scanner or manual entry.",
                          });
                        }}
                        containerStyle={{ width: "100%", height: "100%" }}
                        videoStyle={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />

                      {isProcessing && (
                        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                          <div className="bg-white rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-2 shadow-xl border border-blue-200">
                            <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                            Processing...
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Currently Scanned Devices (Current Session) */}
                {matchedDevices.length > 0 && (
                  <div className="bg-white border border-purple-200 rounded-lg p-1 space-y-2">
                    <div className="flex justify-between items-center px-3 pt-2">
                      <p className="text-xs font-bold text-purple-700 uppercase">
                        Current Session Scans
                      </p>
                      <button
                        id={`warehouse-inward-infra-item-${item.inventoryItemCode}-clear-scanned-modal`}
                        onClick={handleClearAll}
                        className="text-[10px] font-bold text-red-500 hover:text-red-700 uppercase tracking-tighter"
                      >
                        Clear All
                      </button>
                    </div>
                    <DeviceIdsViewer
                      deviceIds={matchedDevices}
                      title={`Currently Scanned Devices`}
                    />
                  </div>
                )}

                {/* Footer */}
                <div className="flex gap-2 pt-4 border-t mt-4">
                  {matchedDevices.length > 0 && (
                    <button
                      id={`warehouse-inward-infra-item-${item.inventoryItemCode}-clear-scanned-footer`}
                      type="button"
                      onClick={handleClearAll}
                      className="flex-1 border border-red-300 text-red-600 rounded-md py-2 text-sm font-semibold hover:bg-red-50 transition"
                    >
                      Clear All
                    </button>
                  )}

                  <button
                    id={`warehouse-inward-infra-item-${item.inventoryItemCode}-done-scanning`}
                    type="button"
                    onClick={() => setScanning(false)}
                    className="flex-1 bg-gray-100 text-gray-700 rounded-md py-2 text-sm font-semibold hover:bg-gray-200 transition"
                  >
                    Done Scanning
                  </button>
                </div>
              </DeviceScanModalInward>
            </div>
          )}

          {/* Currently Scanned Devices (Current Session) */}
          {matchedDevices.length > 0 && (
            <div className="bg-white border border-purple-200 rounded-lg p-1 space-y-2">
              <div className="flex justify-between items-center px-3 pt-2">
                <p className="text-xs font-bold text-purple-700 uppercase">
                  Current Session Scans
                </p>
                <button
                  id={`warehouse-inward-infra-item-${item.inventoryItemCode}-clear-scanned-panel`}
                  onClick={handleClearAll}
                  className="text-[10px] font-bold text-red-500 hover:text-red-700 uppercase tracking-tighter"
                >
                  Clear All
                </button>
              </div>
              <DeviceIdsViewer
                deviceIds={matchedDevices}
                title={`Currently Scanned Devices`}
              />
            </div>
          )}

          {/* Rejected Devices */}
          {rejectedDevices.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-semibold text-red-700">
                  <Icon path={mdiCloseCircle} size={0.7} />
                  Rejected Devices ({rejectedDevices.length})
                </div>
                <button
                  id={`warehouse-inward-infra-item-${item.inventoryItemCode}-clear-rejected`}
                  type="button"
                  onClick={handleClearRejected}
                  className="text-xs text-red-600 hover:underline"
                >
                  Clear List
                </button>
              </div>

              {(() => {
                const grouped = rejectedDevices.reduce(
                  (acc, d) => {
                    if (!acc[d.reason]) acc[d.reason] = [];
                    acc[d.reason].push(d.id);
                    return acc;
                  },
                  {} as Record<string, string[]>,
                );

                return (
                  <div className="space-y-2">
                    {Object.entries(grouped).map(([reason, ids]) => (
                      <div key={reason} className="text-xs">
                        <p className="font-semibold text-red-800 mb-1">
                          {reason} ({ids.length})
                        </p>
                        <div className="max-h-24 overflow-y-auto">
                          <div className="flex flex-wrap gap-1">
                            {ids.slice(0, 10).map((id, i) => (
                              <span
                                key={i}
                                className="px-2 py-0.5 border border-red-300 rounded bg-white font-mono text-xs text-red-700"
                              >
                                {id}
                              </span>
                            ))}
                            {ids.length > 10 && (
                              <span className="text-xs text-red-700 px-2 py-0.5">
                                +{ids.length - 10} more
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
    </div>
  );
};

export default InfraInwardReceiveItemCard;
